import React, { useEffect } from 'react';
import { useQuery, gql } from '@apollo/client';
import { Link } from 'react-router-dom';
import { wpClient, checkConnection } from '../lib/wordpress';

const GET_FEATURED_CATEGORIES = gql`
  query GetFeaturedCategories {
    disabilityHacks(first: 6) {
      nodes {
        databaseId
        title
        categories {
          nodes {
            name
          }
        }
        disabilityHacksImport {
          title
          platform
          videoUrl
          summary
          hackDescription
          otKeywords {
            name
          }
          source
          userTested
          otUsefulnessRating
          hackType
          reviewedBy
          difficultyLevel
          featured
          submittedByEmail
        }
      }
    }
  }
`;

interface CategoryHack {
  databaseId: number;
  title: string;
  categories: {
    nodes: { name: string }[];
  };
  disabilityHacksImport: {
    hackDescription: string;
    videoUrl: string;
    otKeywords: { name: string }[];
  };
}

function CategorySection() {
  useEffect(() => {
    // Check WordPress connection when component mounts
    checkConnection().then(isConnected => {
      console.log('WordPress connection status:', isConnected);
    });
  }, []);

  const { loading, error, data } = useQuery(GET_FEATURED_CATEGORIES, {
    client: wpClient,
    onError: (error) => {
      console.error('GraphQL Error:', error);
      console.error('Error Network:', error.networkError);
      console.error('Error GraphQL:', error.graphQLErrors);
    }
  });

  console.log('Query Response:', { loading, error, data });

  if (loading) return <div className="text-center py-8">Loading categories...</div>;
  if (error) {
    console.error('Detailed error:', error);
    return (
      <div className="text-red-600 text-center py-8">
        Error: {error.message}
        <br />
        <small>Check console for more details</small>
      </div>
    );
  }

  // Shuffle the hacks array to randomize order
  const hacks = (data?.disabilityHacks?.nodes || []).slice();
  for (let i = hacks.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [hacks[i], hacks[j]] = [hacks[j], hacks[i]];
  }

  return (
    <div className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900">
            Explore Hacks by Category
          </h2>
          <p className="mt-4 text-xl text-gray-600">
            Find solutions tailored to your needs
          </p>
        </div>

        <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {hacks.map((hack: CategoryHack) => {
            console.log('Browse Grid Post:', hack.title, 'Categories:', hack.categories?.nodes);
            return (
              <Link
                key={hack.databaseId}
                to={`/hack/${hack.databaseId}`}
                className="block bg-[#f5f7f5] rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="p-6 flex flex-col h-full">
                  <div className="text-green-700 font-medium mb-2 uppercase text-sm">
                    {hack.categories?.nodes.map(node => node.name).join(', ') || 'Uncategorized'}
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {hack.title}
                  </h3>

                  <p className="text-gray-600 mb-4 flex-grow">
                    {hack.disabilityHacksImport?.hackDescription}
                  </p>

                  {hack.disabilityHacksImport?.otKeywords && hack.disabilityHacksImport.otKeywords.slice(0, 3).map((keyword, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-white text-gray-700 rounded-full text-sm"
                    >
                      {keyword.name}
                    </span>
                  ))}
                  {hack.disabilityHacksImport?.otKeywords && hack.disabilityHacksImport.otKeywords.length > 3 && (
                    <span className="text-sm text-gray-600">
                      +{hack.disabilityHacksImport.otKeywords.length - 3} more
                    </span>
                  )}

                  <button className="w-full border-2 border-gray-900 text-gray-900 rounded-lg py-2 px-4 hover:bg-gray-900 hover:text-white transition-colors">
                    View Details
                  </button>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default CategorySection;
